/*
  Adapter between Dawn's cart scripts and the Re-Forge cart drawer.

  Dawn's cart.js and cart-drawer.js do all the real work — AJAX calls to
  /cart/change.js and /cart/add.js, focus trapping, live regions. They only
  hardcode the section handle "cart-drawer" in three places, so this file
  repoints those at "rf-cart-drawer".

  It also takes over the bag count. Dawn re-renders the whole cart-icon-bubble
  section into #cart-icon-bubble, which would replace the Re-Forge bag pill
  ("Bag 2") with Dawn's cart SVG. Instead the count is written straight into
  the pill and the section is left out of the render list.

  Loaded after cart.js and cart-drawer.js, so CartDrawer and CartDrawerItems
  are already defined.
*/

(function () {
  const SECTION = 'rf-cart-drawer';

  if (typeof CartDrawer === 'undefined' || typeof CartDrawerItems === 'undefined') return;

  function updateBagCount(count) {
    if (typeof count !== 'number') return;
    document.querySelectorAll('#cart-icon-bubble i, .rf-cartpill i').forEach((el) => {
      el.textContent = count;
    });
  }

  function refreshBagCount() {
    if (typeof CartItems === 'undefined' || !CartItems.fetchCartData) return;
    CartItems.fetchCartData().then((cart) => {
      if (cart) updateBagCount(cart.item_count);
    });
  }

  // Sections pulled after /cart/add.js, and written back into the DOM.
  CartDrawer.prototype.getSectionsToRender = function () {
    return [{ id: SECTION, selector: '#CartDrawer' }];
  };

  // The /cart page. Dawn's list includes cart-icon-bubble, and it writes that
  // section's HTML straight into #cart-icon-bubble — which here is the Re-Forge
  // bag pill, so the "Bag 2" label would be replaced by Dawn's cart glyph.
  // Dropped from the list; the count is written in by hand below instead.
  CartItems.prototype.getSectionsToRender = function () {
    const sections = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');

    if (items) sections.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (document.getElementById('cart-live-region-text')) {
      sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }
    if (footer) sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });

    return sections;
  };

  // Sections pulled after /cart/change.js (quantity steppers, remove).
  CartDrawerItems.prototype.getSectionsToRender = function () {
    return [{ id: 'CartDrawer', section: SECTION, selector: '.drawer__inner' }];
  };

  // Dawn's version fetches ?section_id=cart-drawer. Same logic, our handle.
  CartDrawerItems.prototype.onCartUpdate = function () {
    return fetch(`${routes.cart_url}?section_id=${SECTION}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
          const target = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });
      })
      .catch((e) => console.error(e));
  };

  // /cart/add.js returns the added line, not the cart, so the count is read back.
  const renderContents = CartDrawer.prototype.renderContents;
  CartDrawer.prototype.renderContents = function (parsedState) {
    renderContents.call(this, parsedState);
    refreshBagCount();
  };

  // /cart/change.js returns the full cart, so the count comes with it.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.cartData && typeof event.cartData.item_count === 'number') {
        updateBagCount(event.cartData.item_count);
      } else {
        refreshBagCount();
      }

      // Dawn's drawer ignores updates published by the cart page, which leaves
      // the drawer showing stale lines if it is opened afterwards on /cart.
      if (event && event.source === 'cart-items') {
        const drawerItems = document.querySelector('cart-drawer-items');
        if (drawerItems && drawerItems.onCartUpdate) drawerItems.onCartUpdate();
      }
    });
  }

  // Cross-sell: one product, added over AJAX, drawer re-rendered in place.
  document.addEventListener('click', function (event) {
    const button = event.target.closest('.rf-cart__xsell-add');
    if (!button) return;

    const variantId = button.dataset.variantId;
    const drawer = document.querySelector('cart-drawer');
    if (!variantId || !drawer) return;

    button.setAttribute('aria-disabled', 'true');

    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: drawer.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsed) => {
        if (parsed.status) {
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = parsed.description || parsed.message;
          return;
        }
        drawer.renderContents(parsed);
      })
      .catch((e) => console.error(e))
      .finally(() => button.removeAttribute('aria-disabled'));
  });

  // Back from checkout. The browser can hand back the page exactly as it was
  // left (bfcache) or from its HTTP cache, so the drawer and the bag count can
  // show the bag from before checkout, even an empty one while the cart holds
  // items. On a back/forward return the cart is read again and the drawer
  // redrawn from it.
  function resyncCartFromServer() {
    const drawer = document.querySelector('cart-drawer');
    const noCache = { cache: 'no-store', credentials: 'same-origin' };

    fetch(`${routes.cart_url}.json`, noCache)
      .then((response) => response.json())
      .then((cart) => {
        updateBagCount(cart.item_count);
        if (!drawer) return null;
        drawer.classList.toggle('is-empty', cart.item_count === 0);
        return fetch(`${routes.cart_url}?section_id=${SECTION}&t=${Date.now()}`, noCache)
          .then((response) => response.text())
          .then((responseText) => {
            const source = new DOMParser().parseFromString(responseText, 'text/html').querySelector('#CartDrawer');
            const target = document.querySelector('#CartDrawer');
            if (!source || !target) return;
            target.innerHTML = source.innerHTML;
            const overlay = drawer.querySelector('#CartDrawer-Overlay');
            if (overlay && drawer.close) overlay.addEventListener('click', drawer.close.bind(drawer));
          });
      })
      .catch((e) => console.error(e));
  }

  window.addEventListener('pageshow', function (event) {
    const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    const returned = event.persisted || (navigation && navigation.type === 'back_forward');
    if (returned) resyncCartFromServer();
  });
})();
